import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError, patch } from "../../lib/api";
import { DISPLAY_LABEL_MAX } from "../../lib/station";
import type { ChargePoint } from "../../lib/types";
import { Button } from "../ui/Button";
import { Notice } from "../ui/Feedback";
import { Field } from "../ui/Input";
import { SettingRow } from "../ui/SettingRow";

/**
 * What a driver sees this station called. pay-ui spec §7.1, §13.3.4.
 *
 * ## Why an operator needs this at all
 *
 * Until now the driver's screens named a station by its charge point id and the
 * connector number — `HLH_STATION_0001 · 2`. That is the string on the sticker and
 * it is the right fallback, but it is not what the place is called. Somebody
 * standing in a car park is checking they are at the right charger, and "Riverside
 * Mall — Bay 3" answers that where an asset tag does not.
 *
 * Clearing the name is a first-class action rather than "delete the text and save":
 * the fallback is keyed on the column being null, so a station named with an empty
 * string would show a driver an empty nameplate. The server refuses that; this
 * offers *Remove name* so an operator never has to discover it.
 *
 * ## Its own row, beside the price rather than inside it
 *
 * `PublicAccessPanel` is one form because `charge_point_public_needs_tariff` makes
 * the switch and the price one decision. The name is not part of that decision — it
 * is worth setting before a station ever goes on sale, and worth fixing without
 * touching commercial state — so it is a separate form on the same endpoint.
 *
 * The character ceiling is the nameplate's, not the column's: pay-ui renders this
 * on a 320 px screen, and past that it wraps to three lines. It lives in
 * `lib/station.ts` beside the registration form's copy of the same limit, and is
 * enforced again in the route and the column.
 */

export function StationName({
  station,
  canEdit,
}: {
  readonly station: ChargePoint;
  readonly canEdit: boolean;
}) {
  const client = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(station.displayLabel ?? "");
  const [failed, setFailed] = useState<string | undefined>();

  const save = useMutation({
    mutationFn: (displayLabel: string | null) =>
      patch<ChargePoint>(`/charge-points/${encodeURIComponent(station.id)}`, { displayLabel }),
    onSuccess: (updated) => {
      setIsEditing(false);
      setFailed(undefined);
      setDraft(updated.displayLabel ?? "");
      // The fleet table shows this station too, and a name that disagrees between
      // the table and the page is worse than a name only in one of them.
      void client.invalidateQueries({ queryKey: ["charge-point", station.id] });
      void client.invalidateQueries({ queryKey: ["charge-points"] });
    },
    onError: (error: unknown) => {
      // The server's own sentence: it is the one that knows which of the three
      // checks refused. Same rule as `PublicAccessPanel`.
      setFailed(
        error instanceof ApiError ? error.message : "The name could not be saved. Try again.",
      );
    },
  });

  const trimmed = draft.trim();
  const isNamed = station.displayLabel !== null;

  return (
    <SettingRow
      label="Driver-facing name"
      value={isNamed ? `“${station.displayLabel}”` : "Unnamed"}
      hint={
        isNamed
          ? "Shown on the pay screen and on the driver's receipt."
          : `A driver scanning the sticker sees this station's id — ${station.id} — and the connector number.`
      }
      actions={
        !canEdit || isEditing ? null : (
          <>
            <Button variant={isNamed ? "secondary" : "primary"} onClick={() => setIsEditing(true)}>
              {isNamed ? "Change name" : "Name this station"}
            </Button>
            {isNamed ? (
              <Button
                variant="ghost"
                pending={save.isPending}
                onClick={() => {
                  setFailed(undefined);
                  // `null`, not `""`. The screens fall back to the id on null; an
                  // empty string is refused by the route and by the column.
                  save.mutate(null);
                }}
              >
                Remove name
              </Button>
            ) : null}
          </>
        )
      }
    >
      {failed === undefined ? null : <Notice tone="error">{failed}</Notice>}

      {isEditing ? (
        <>
          <Field
            label="Name"
            value={draft}
            maxLength={DISPLAY_LABEL_MAX}
            placeholder="Riverside Mall — Bay 3"
            hint={`What the place is called, as a driver would say it. ${DISPLAY_LABEL_MAX} characters at most.`}
            disabled={save.isPending}
            onChange={(event) => setDraft(event.target.value)}
          />

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              pending={save.isPending}
              disabled={trimmed === "" || trimmed === station.displayLabel}
              onClick={() => {
                setFailed(undefined);
                save.mutate(trimmed);
              }}
            >
              Save name
            </Button>
            <Button
              disabled={save.isPending}
              onClick={() => {
                setIsEditing(false);
                setFailed(undefined);
                setDraft(station.displayLabel ?? "");
              }}
            >
              Cancel
            </Button>
          </div>
        </>
      ) : null}
    </SettingRow>
  );
}
